import { HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap, withLatestFrom } from 'rxjs';
import * as ProgramActions from './program.actions';
import { selectPrograms, selectProgramsQuery } from './program.selectors';
import { ProgramService } from '../services/program.service';
import { ProgramDto } from '@shared/dto';
import { listResultActions$ } from '@shared/utils/pagination.utils';

@Injectable()
export class ProgramEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly programService = inject(ProgramService);

  readPrograms$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramActions.readPrograms),
    exhaustMap(({ query }) =>
      listResultActions$(
        query ? this.programService.read(undefined, { pagination: query }) : this.programService.read(),
        query,
        ({ data, total }) => ProgramActions.readProgramsSuccess({ data, total }),
        (error) => ProgramActions.readProgramsFailure({ error }),
      )),
  ));

  // Detail: resolve from the already-loaded list first (no HTTP), otherwise fetch
  // the single entity. The store is the source of truth that both the detail page
  // and the breadcrumb read from.
  readProgram$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramActions.readProgram),
    withLatestFrom(this.store.select(selectPrograms)),
    switchMap(([{ programId }, list]) => {
      const cached = list?.find((program) => program.id === programId);
      if (cached) {
        return of(ProgramActions.readProgramSuccess({ program: cached }));
      }

      return this.programService.readOne({ programId }).pipe(
        map((program) => ProgramActions.readProgramSuccess({ program })),
        catchError((error) => of(ProgramActions.readProgramFailure({ error }))),
      );
    }),
  ));

  createProgram$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramActions.createProgram),
    exhaustMap(({ dto }) => this.programService.create(dto).pipe(
      map((therapy: ProgramDto) => ProgramActions.createProgramSuccess({ program: therapy })),
      catchError((error) => of(ProgramActions.createProgramFailure({ error })))
    ))
  ));

  updateProgram$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramActions.updateProgram),
    exhaustMap(({ programId, dto }) => this.programService.update({ programId }, dto).pipe(
      map((therapy: Partial<ProgramDto>) => ProgramActions.updateProgramSuccess({ program: therapy })),
      catchError((error) => {
        // 428 (PROGRAM.UPDATE_REQUIRES_CONFIRMATION) = the edit reaches a shared TEMPLATE
        // session. Not a failure: surface it as "requires confirmation" so the page can offer
        // the clone dialog. Any other status stays an ordinary failure.
        if (error?.status === HttpStatusCode.PreconditionRequired) {
          return of(ProgramActions.updateProgramRequiresConfirmation({ programId, dto, message: error.message }));
        }
        return of(ProgramActions.updateProgramFailure({ error }));
      })
    ))
  ));

  deleteProgram$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramActions.deleteProgram),
    exhaustMap(({ programId, confirm }) => this.programService.delete(
      { programId },
      confirm ? { params: { confirm: 'true' } } : undefined,
    ).pipe(
      map((therapy: ProgramDto) => ProgramActions.deleteProgramSuccess({ program: therapy })),
      catchError((error) => {
        // 428 = active assignments exist; not a failure, let the page confirm and resend
        // `confirm=true`. Any other status (incl. 403 not-owner, 409 referenced) stays a
        // failure — its message is surfaced by the toast.
        if (error?.status === HttpStatusCode.PreconditionRequired) {
          return of(ProgramActions.deleteProgramRequiresConfirmation({ programId, message: error.message }));
        }
        return of(ProgramActions.deleteProgramFailure({ error }));
      })
    ))
  ));

  restoreProgram$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramActions.restoreProgram),
    exhaustMap(({ programId }) => this.programService.restore({ programId }).pipe(
      map((therapy: ProgramDto) => ProgramActions.restoreProgramSuccess({ program: therapy })),
      catchError((error) => of(ProgramActions.restoreProgramFailure({ error })))
    ))
  ));

  // After a mutation, refetch the current page so counts/pagination stay correct.
  reloadAfterMutation$ = createEffect(() => this.actions$.pipe(
    ofType(
      ProgramActions.createProgramSuccess,
      ProgramActions.updateProgramSuccess,
      ProgramActions.deleteProgramSuccess,
      ProgramActions.restoreProgramSuccess,
    ),
    withLatestFrom(this.store.select(selectProgramsQuery)),
    map(([, query]) => ProgramActions.readPrograms(query ?? undefined)),
  ));
}
